// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/pages/JournalListPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// PAGE:
// Manual Journal List
//
// PURPOSE:
// Displays Manual Journal transactions.
//
// FEATURES:
// - Responsive UI
// - KPI Summary
// - Search & Filters
// - Journal Grid
// - Mobile Cards
// - Pagination
// - Workflow Actions
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";

import AsyncSelect from "react-select/async";
import Select from "react-select";

import FinancePageHeader from "../../../components/FinancePageHeader";
import FinanceSummaryCards from "../../../components/FinanceSummaryCards";
import FinanceSearchPanel from "../../../components/FinanceSearchPanel";
import FinanceStatusBadge from "../../../components/FinanceStatusBadge";
import FinanceActionButtons from "../../../components/FinanceActionButtons";

import journalService from "../../../services/journalService";
import financeLookupService from "../../../services/financeLookupService";
// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalListPage() {

    const navigate = useNavigate();

    // ==============================================================
    // LOADING
    // ==============================================================

    const [
        loading,
        setLoading,
    ] = useState(false);

    // ==============================================================
    // DATA
    // ==============================================================

    const [
        journals,
        setJournals,
    ] = useState([]);

    const [
        summary,
        setSummary,
    ] = useState({

        total: 0,

        draft: 0,

        submitted: 0,

        approved: 0,

        posted: 0,

        reversed: 0,

        cancelled: 0,

    });

    const [
        totalRecords,
        setTotalRecords,
    ] = useState(0);

    // ==============================================================
    // PAGINATION
    // ==============================================================

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        perPage,
        setPerPage,
    ] = useState(20);

    const [
        totalPages,
        setTotalPages,
    ] = useState(1);

    // ==============================================================
    // FILTERS
    // ==============================================================

    const [
        filters,
        setFilters,
    ] = useState({

        journal_no: "",

        company_id: null,

        financial_year_id: null,

        financial_period_id: null,

        journal_type: "",

        status: "",

        date_from: "",

        date_to: "",

    });
    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);
    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);
    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);
    // ==============================================================
    // JOURNAL TYPES
    // ==============================================================

    const journalTypeOptions = useMemo(
        () => [

            {
                value: "",
                label: "All",
            },

            {
                value: "MANUAL",
                label: "Manual",
            },

            {
                value: "PAYMENT",
                label: "Payment",
            },

            {
                value: "RECEIPT",
                label: "Receipt",
            },

            {
                value: "CONTRA",
                label: "Contra",
            },

            {
                value: "CREDIT_NOTE",
                label: "Credit Note",
            },

            {
                value: "DEBIT_NOTE",
                label: "Debit Note",
            },

        ],
        []
    );

    // ==============================================================
    // STATUS OPTIONS
    // ==============================================================

    const statusOptions = useMemo(
        () => [

            {
                value: "",
                label: "All",
            },

            {
                value: "DRAFT",
                label: "Draft",
            },

            {
                value: "APPROVED",
                label: "Approved",
            },

            {
                value: "POSTED",
                label: "Posted",
            },

            {
                value: "REVERSED",
                label: "Reversed",
            },

            {
                value: "CANCELLED",
                label: "Cancelled",
            },

        ],
        []
    );
        // ==============================================================
    // LOAD JOURNALS
    // ==============================================================

    useEffect(() => {

        loadJournals();

    }, []);

    // ==============================================================
    // LOAD JOURNALS
    // ==============================================================

    async function loadJournals(
        appliedFilters = filters,
        currentPage = page,
        currentPerPage = perPage
    ) {

        try {

            setLoading(true);

            const result =
                await journalService.listJournals({

                    ...appliedFilters,

                    page: currentPage,

                    per_page: currentPerPage,

                });

            setJournals(
                result.rows || []
            );

            setSummary(
                result.summary || {}
            );

            setTotalRecords(
                result.pagination?.total || 0
            );

            setTotalPages(
                result.pagination?.pages || 1
            );

            setPage(
                result.pagination?.page || 1
            );

            setPerPage(
                result.pagination?.per_page || 20
            );

        }

        catch (error) {

            console.error(
                "Failed to load journals.",
                error
            );

            setJournals([]);

            setSummary({

                total: 0,

                draft: 0,

                submitted: 0,

                approved: 0,

                posted: 0,

                reversed: 0,

                cancelled: 0,

            });

            setTotalRecords(0);

            setTotalPages(1);

        }

        finally {

            setLoading(false);

        }

    }

    // ==============================================================
    // UPDATE FILTER
    // ==============================================================

    function updateFilter(
        field,
        value
    ) {

        setFilters(

            previous => ({

                ...previous,

                [field]: value,

            })

        );

    }

    // ==============================================================
    // SEARCH
    // ==============================================================

    function handleSearch() {

        loadJournals(
            filters,
            1,
            perPage
        );

    }

    // ==============================================================
    // RESET
    // ==============================================================

    function handleReset() {

        const resetFilters = {

            journal_no: "",

            company_id: null,

            financial_year_id: null,

            financial_period_id: null,

            journal_type: "",

            status: "",

            date_from: "",

            date_to: "",

        };

        setFilters(
            resetFilters
        );

        loadJournals(
            resetFilters,
            1,
            20
        );

    }

    // ==============================================================
    // PAGINATION
    // ==============================================================

    function handlePageChange(
        newPage
    ) {

        loadJournals(

            filters,

            newPage,

            perPage

        );

    }

    function handlePageSizeChange(
        event
    ) {

        const size =
            Number(
                event.target.value
            );

        loadJournals(

            filters,

            1,

            size

        );

    }

    // ==============================================================
    // NAVIGATION
    // ==============================================================

    function handleCreate() {

        navigate(
            "/finance/manual-journal/create"
        );

    }

    function handleView(
        row
    ) {

        navigate(
            `/finance/manual-journal/view/${row.id}`
        );

    }

    function handleEdit(
        row
    ) {

        navigate(
            `/finance/manual-journal/edit/${row.id}`
        );

    }

    // ==============================================================
    // DELETE
    // ==============================================================

    async function handleDelete(
        row
    ) {

        if (

            !window.confirm(

                "Delete selected journal?"

            )

        ) {

            return;

        }

        await journalService.deleteJournal(
            row.id
        );

        loadJournals();

    }

    // ==============================================================
    // SUBMIT
    // ==============================================================

    async function handleSubmit(
        row
    ) {

        await journalService.submitJournal(
            row.id
        );

        loadJournals();

    }

    // ==============================================================
    // APPROVE
    // ==============================================================

    async function handleApprove(
        row
    ) {

        await journalService.approveJournal(
            row.id
        );

        loadJournals();

    }

    // ==============================================================
    // REJECT
    // ==============================================================

    async function handleReject(
        row
    ) {

        await journalService.rejectJournal(
            row.id
        );

        loadJournals();

    }

    // ==============================================================
    // POST
    // ==============================================================

    async function handlePost(
        row
    ) {

        await journalService.postJournal(
            row.id
        );

        loadJournals();

    }
        // ==============================================================
    // ASYNC DROPDOWN LOADERS
    // ==============================================================

    async function loadCompanies(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService.searchCompanies(
                    inputValue
                );

            return (
                response || []
            ).map(
                company => ({

                    value: company.id,

                    label: company.company_name,

                })
            );

        }

        catch {

            return [];

        }

    }

    async function loadFinancialYears(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService.searchFinancialYears(
                    inputValue
                );

            return (
                response || []
            ).map(
                year => ({

                    value: year.id,

                    label: year.year_name,

                })
            );

        }

        catch {

            return [];

        }

    }

    const loadFinancialPeriods = async (
    inputValue
) => {

    if (!selectedFinancialYear) {
        return [];
    }

    try {

        const response =
            await financeLookupService
                .searchFinancialPeriods(
                    selectedFinancialYear.value,
                    inputValue
                );

        return response.map(
            (item) => ({
                value: item.id,
                label: item.period_name,
            })
        );

    } catch (error) {

        console.error(
            "Failed to load financial periods:",
            error
        );

        return [];
    }
};

    // ==============================================================
    // SUMMARY CARDS
    // ==============================================================

    const summaryCards = [

        {
            key: "total",
            title: "Total Journals",
            value: summary.total,
            icon: "fas fa-book",
            background: "#0d6efd",
        },

        {
            key: "draft",
            title: "Draft",
            value: summary.draft,
            icon: "fas fa-pencil-alt",
            background: "#6c757d",
        },

        {
            key: "submitted",
            title: "Submitted",
            value: summary.submitted,
            icon: "fas fa-paper-plane",
            background: "#ffc107",
        },

        {
            key: "approved",
            title: "Approved",
            value: summary.approved,
            icon: "fas fa-check",
            background: "#198754",
        },

        {
            key: "posted",
            title: "Posted",
            value: summary.posted,
            icon: "fas fa-check-double",
            background: "#0dcaf0",
        },

    ];

    // ==============================================================
    // RENDER
    // ==============================================================

    return (

        <>

            <FinancePageHeader

                icon="fas fa-book"

                title="Manual Journal"

                subtitle="Create and manage accounting journals."

                breadcrumbs={[

                    {
                        label: "Finance",
                        to: "/finance",
                    },

                    {
                        label: "Manual Journal",
                    },

                ]}

                actions={

                    <>

                        <button
                            className="btn btn-outline-success"
                        >

                            <i className="fas fa-file-export me-2" />

                            Export

                        </button>

                        <button
                            className="btn btn-primary"
                            onClick={handleCreate}
                        >

                            <i className="fas fa-plus me-2" />

                            New Journal

                        </button>

                    </>

                }

            />

            <FinanceSummaryCards

                cards={summaryCards}

            />

            <FinanceSearchPanel

                loading={loading}

                onSearch={handleSearch}

                onReset={handleReset}

            >

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Journal No

                    </label>

                    <input

                        className="form-control"

                        value={filters.journal_no}

                        onChange={(e) =>
                            updateFilter(
                                "journal_no",
                                e.target.value
                            )
                        }

                        placeholder="Journal Number"

                    />

                </div>

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Company

                    </label>

                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={loadCompanies}

                        isClearable

                        value={selectedCompany}

                        onChange={(option) => {

                            setSelectedCompany(option);

                            updateFilter(
                                "company_id",
                                option?.value || null
                            );
                        }}

                    />

                </div>

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Financial Year

                    </label>

                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={
                            loadFinancialYears
                        }

                        isClearable

                        value={selectedFinancialYear}

                        onChange={(option) => {

                        setSelectedFinancialYear(option);

                        updateFilter(
                            "financial_year_id",
                            option?.value || null
                        );
                    }}

                    />

                </div>

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Financial Period

                    </label>

                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={
                            loadFinancialPeriods
                        }

                        isClearable

                        value={selectedFinancialPeriod}

                        onChange={(option) => {

                            setSelectedFinancialPeriod(option);

                            updateFilter(
                                "financial_period_id",
                                option?.value || null
                            );
                        }}

                    />

                </div>
                                {/* ======================================================
                    JOURNAL TYPE
                ====================================================== */}

                <div className="col-lg-2 col-md-6">

                    <label className="form-label">

                        Journal Type

                    </label>

                    <Select

                        options={journalTypeOptions}

                        value={
                            journalTypeOptions.find(
                                option =>
                                    option.value ===
                                    filters.journal_type
                            )
                        }

                        onChange={(option) =>

                            updateFilter(

                                "journal_type",

                                option?.value || ""

                            )

                        }

                    />

                </div>

                {/* ======================================================
                    STATUS
                ====================================================== */}

                <div className="col-lg-2 col-md-6">

                    <label className="form-label">

                        Status

                    </label>

                    <Select

                        options={statusOptions}

                        value={
                            statusOptions.find(
                                option =>
                                    option.value ===
                                    filters.status
                            )
                        }

                        onChange={(option) =>

                            updateFilter(

                                "status",

                                option?.value || ""

                            )

                        }

                    />

                </div>

                {/* ======================================================
                    DATE FROM
                ====================================================== */}

                <div className="col-lg-2 col-md-6">

                    <label className="form-label">

                        Date From

                    </label>

                    <input

                        type="date"

                        className="form-control"

                        value={filters.date_from}

                        onChange={(e) =>

                            updateFilter(

                                "date_from",

                                e.target.value

                            )

                        }

                    />

                </div>

                {/* ======================================================
                    DATE TO
                ====================================================== */}

                <div className="col-lg-2 col-md-6">

                    <label className="form-label">

                        Date To

                    </label>

                    <input

                        type="date"

                        className="form-control"

                        value={filters.date_to}

                        onChange={(e) =>

                            updateFilter(

                                "date_to",

                                e.target.value

                            )

                        }

                    />

                </div>

            </FinanceSearchPanel>

            {/* ======================================================
                JOURNAL GRID
            ====================================================== */}

            <div className="card shadow-sm border-0">

                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">

                                <tr>

                                    <th style={{ width: 60 }}>

                                        #

                                    </th>

                                    <th>

                                        Journal No

                                    </th>

                                    <th>

                                        Date

                                    </th>

                                    <th>

                                        Company

                                    </th>

                                    <th>

                                        Type

                                    </th>

                                    <th className="text-end">

                                        Amount

                                    </th>

                                    <th>

                                        Currency

                                    </th>

                                    <th>

                                        Status

                                    </th>

                                    <th>

                                        Approval

                                    </th>

                                    <th
                                        className="text-center"
                                        style={{
                                            width: 220,
                                        }}
                                    >

                                        Actions

                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {

                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        spinner-border
                                                        text-primary
                                                    "
                                                />

                                            </td>

                                        </tr>

                                    ) : journals.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-book
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>

                                                        No Journals Found

                                                    </h5>

                                                    <p>

                                                        Create your first
                                                        Manual Journal.

                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : (

                                        journals.map(

                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={row.id}
                                                >

                                                    <td>

                                                        {

                                                            (
                                                                (page - 1) *
                                                                perPage
                                                            ) +
                                                            index +
                                                            1

                                                        }

                                                    </td>

                                                    <td>

                                                        <button

                                                            className="
                                                                btn
                                                                btn-link
                                                                p-0
                                                            "

                                                            onClick={() =>
                                                                handleView(
                                                                    row
                                                                )
                                                            }

                                                        >

                                                            {

                                                                row.journal_no

                                                            }

                                                        </button>

                                                    </td>

                                                    <td>

                                                        {

                                                            row.journal_date

                                                        }

                                                    </td>

                                                    <td>

                                                        {

                                                            row.company_name

                                                        }

                                                    </td>

                                                    <td>

                                                        {

                                                            row.journal_type

                                                        }

                                                    </td>

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {

                                                            Number(
                                                                row.transaction_amount || 0
                                                            ).toLocaleString()

                                                        }

                                                    </td>

                                                    <td>

                                                        {

                                                            row.currency_code

                                                        }

                                                    </td>

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="journal"

                                                            value={
                                                                row.status
                                                            }

                                                        />

                                                    </td>

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="approval"

                                                            value={
                                                                row.approval_status
                                                            }

                                                        />

                                                    </td>

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <FinanceActionButtons

                                                            row={row}

                                                            onView={handleView}

                                                            onEdit={handleEdit}

                                                            onDelete={handleDelete}

                                                            onSubmit={handleSubmit}

                                                            onApprove={handleApprove}

                                                            onReject={handleReject}

                                                            onPost={handlePost}

                                                        />

                                                    </td>

                                                </tr>

                                            )

                                        )

                                    )

                                }

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>
                        {/* ======================================================
                PAGINATION
            ====================================================== */}

            <div
                className="
                    d-flex
                    flex-wrap
                    justify-content-between
                    align-items-center
                    mt-4
                    gap-3
                "
            >

                <div>

                    <small
                        className="text-muted"
                    >

                        Total Records :
                        <strong className="ms-1">

                            {totalRecords}

                        </strong>

                    </small>

                </div>

                <div
                    className="
                        d-flex
                        align-items-center
                        gap-3
                    "
                >

                    {/* ==========================================
                        PAGE SIZE
                    ========================================== */}

                    <div
                        className="
                            d-flex
                            align-items-center
                            gap-2
                        "
                    >

                        <label
                            className="mb-0"
                        >

                            Rows

                        </label>

                        <select

                            className="form-select"

                            style={{
                                width: 90,
                            }}

                            value={perPage}

                            onChange={
                                handlePageSizeChange
                            }

                        >

                            <option value={10}>

                                10

                            </option>

                            <option value={20}>

                                20

                            </option>

                            <option value={50}>

                                50

                            </option>

                            <option value={100}>

                                100

                            </option>

                        </select>

                    </div>

                    {/* ==========================================
                        PAGE INFO
                    ========================================== */}

                    <span
                        className="text-muted"
                    >

                        Page

                        <strong
                            className="mx-1"
                        >

                            {page}

                        </strong>

                        of

                        <strong
                            className="ms-1"
                        >

                            {totalPages}

                        </strong>

                    </span>

                    {/* ==========================================
                        PREVIOUS
                    ========================================== */}

                    <button

                        className="
                            btn
                            btn-outline-secondary
                        "

                        disabled={
                            page <= 1
                        }

                        onClick={() =>

                            handlePageChange(
                                page - 1
                            )

                        }

                    >

                        <i
                            className="
                                fas
                                fa-chevron-left
                            "
                        />

                    </button>

                    {/* ==========================================
                        NEXT
                    ========================================== */}

                    <button

                        className="
                            btn
                            btn-outline-secondary
                        "

                        disabled={
                            page >= totalPages
                        }

                        onClick={() =>

                            handlePageChange(
                                page + 1
                            )

                        }

                    >

                        <i
                            className="
                                fas
                                fa-chevron-right
                            "
                        />

                    </button>

                </div>

            </div>

        </>

    );

}